import React from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    Typography,
    Chip,
    Avatar,
    Button,
    CircularProgress,
    Badge,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    AttachFile as AttachFileIcon,
    Error as ErrorIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import moment from 'moment';
import { hideEmail } from 'utils/user';

const EmailList = ({
    emails,
    selectedEmailId,
    onEmailSelect,
    onLoadMore,
    hasNextPage,
    loading
}) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getAttachmentStatusIcon = (email) => {
    const status = email.attachmentProcessingStatus;

    if (!status || status.total === 0) return null;

    if (status.hasFailures) {
      return <ErrorIcon color="error" fontSize="small" />;
    }

    if (status.pending > 0) {
      return <ScheduleIcon color="warning" fontSize="small" />;
    }

    if (status.allProcessed) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    }

    return <AttachFileIcon color="action" fontSize="small" />;
  };

  const getAttachmentCount = (email) => {
    // Check multiple possible fields for attachment count
    if (email.attachmentsCount > 0) {
      return email.attachmentsCount;
    }
    if (email.attachmentProcessingStatus?.total > 0) {
      return email.attachmentProcessingStatus.total;
    }
    if (email.processedFiles?.length > 0 || email.failedAttachmentsInfo?.length > 0) {
      return (email.processedFiles?.length || 0) + (email.failedAttachmentsInfo?.length || 0);
    }
    if (email.attachments?.length > 0) {
      return email.attachments.length;
    }
    return null;
  };

  const getSenderDisplay = (email) => {
    if (email.isFromRegisteredUser && email.senderUser?.name) {
      return email.senderUser.name;
    }

    // Show fromName if available, otherwise show the masked email address
    return email.fromName || (email.fromEmail ? hideEmail(email.fromEmail) : 'Unknown');
  };

  const getSenderInitials = (email) => {
    const name = getSenderDisplay(email);
    if (!name) return '??';

    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getItemStyle = (email, isSelected) => {
    if (isMobile) {
      const isUnread = !email.isReadByMe;
      return {
        backgroundColor: isUnread ? '#fff' : '#fcfcfc',
        borderBottom: '1px solid #f1f5f9',
        cursor: 'pointer',
        borderLeft: isUnread ? '4px solid #D24724' : '4px solid transparent',
        transition: 'all 0.2s ease',
        px: 2,
        py: 2,
        '&:active': { backgroundColor: '#f5f5f5' }
      };
    }

    return {
      backgroundColor: isSelected ? '#fcf4f2' : (email.isReadByMe ? '#fafbfc' : 'white'),
      borderLeft: isSelected ? '4px solid #D24724' : '4px solid transparent',
      cursor: 'pointer',
      borderBottom: '1px solid #e6e6e6',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: isSelected ? '#fcf4f2' : '#f7f8fd',
      },
      position: 'relative',
      '&::before': email.isReadByMe ? {} : {
        content: '""',
        position: 'absolute',
        left: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#D24724'
      }
    };
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <List sx={{
        flexGrow: 1,
        overflow: 'auto',
        overflowX: 'hidden',
        padding: 0,
        paddingBottom: hasNextPage ? '80px' : '0',
        '&::-webkit-scrollbar': {
          width: '6px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f2f2f2'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#cccccc',
          borderRadius: '4px'
        }
      }}>
        {emails.map((email, index) => {
          const isSelected = email.id === selectedEmailId;
          const attachmentIcon = getAttachmentStatusIcon(email);
          const attachmentCount = getAttachmentCount(email);
          const isUnread = !email.isReadByMe;

          return (
            <ListItem
              key={email.id}
              data-tour-id={index === 0 ? 'inbox.first-email' : undefined}
              onClick={() => onEmailSelect(email)}
              sx={getItemStyle(email, isSelected)}
              disablePadding={isMobile}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 1.5,
                pl: isMobile ? 0 : (email.isReadByMe ? 1 : 2.5)
              }}>
                {/* Sender Avatar - Comun */}
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    fontSize: '0.875rem',
                    backgroundColor: email.isFromRegisteredUser ? '#D24724' : '#b2b2b2',
                    fontFamily: 'FuturaPT-Book, Open Sans, sans-serif'
                  }}
                >
                  {getSenderInitials(email)}
                </Avatar>

                {/* Email Content */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {isMobile ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: isUnread ? 700 : 500, color: '#1e293b', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%', fontFamily: 'FuturaPT-Book, Open Sans, sans-serif' }}>
                          {getSenderDisplay(email)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isUnread ? '#D24724' : '#94a3b8', fontWeight: isUnread ? 600 : 400, fontSize: '0.75rem' }}>
                          {moment(email.receivedAt).fromNow(true)}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ fontWeight: isUnread ? 600 : 400, color: '#334155', fontSize: '0.9rem', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'FuturaPT-Book, Open Sans, sans-serif' }}>
                        {email.subject || '(No Subject)'}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85%', fontFamily: 'FuturaPT-Book, Open Sans, sans-serif' }}>
                          {email.bodyText?.slice(0, 80) || 'No content preview'}
                        </Typography>
                        {attachmentCount > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f1f5f9', px: 0.8, py: 0.2, borderRadius: 1 }}>
                            <AttachFileIcon sx={{ fontSize: 12, color: '#64748b', mr: 0.5 }} />
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>{attachmentCount}</Typography>
                          </Box>
                        )}
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Header Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: email.isReadByMe ? 'normal' : 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px',
                            fontFamily: 'FuturaPT-Book, Open Sans, sans-serif'
                          }}
                        >
                          {getSenderDisplay(email)}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {moment(email.receivedAt).fromNow()}
                        </Typography>
                      </Box>

                      {/* Subject */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: email.isReadByMe ? 'normal' : 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                            flexGrow: 1
                          }}
                        >
                          {email.subject || '(No Subject)'}
                        </Typography>
                        {attachmentCount && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <AttachFileIcon sx={{ fontSize: '1rem', color: '#84889C' }} />
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#84889C', fontWeight: 600 }}>
                              {attachmentCount}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Preview and Metadata */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '280px'
                          }}
                        >
                          {email.bodyText?.slice(0, 150) || email.bodyHtml?.replace(/<[^>]*>/g, '').slice(0, 150) || email.subject?.slice(0, 150) || 'No content'}
                        </Typography>

                      </Box>

                      {/* Read Status Indicator */}
                      {email.readCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Read by {email.readCount} Building Passport user{email.readCount > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </ListItem>
          );
        })}

        {/* Empty State */}
        {emails.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No emails yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When emails are sent to this Building Passport, they'll appear here.
            </Typography>
          </Box>
        )}
      </List>

      {/* Load More Button - Fixed at bottom */}
      {hasNextPage && (
        <Box sx={{
          p: 1.5,
          textAlign: 'center',
          borderTop: '1px solid #e6e6e6',
          bgcolor: 'white',
          position: 'sticky',
          bottom: 0,
          zIndex: 1
        }}>
          <Button
            onClick={onLoadMore}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            variant="outlined"
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
              borderColor: '#D24724',
              color: '#D24724',
              '&:hover': {
                borderColor: '#b13e1f',
                backgroundColor: '#fcf4f2'
              }
            }}
          >
            {loading ? 'Loading...' : 'Load More Messages'}
          </Button>
        </Box>
      )}

    </Box>
  );
};

export default EmailList;
